import { useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import { NaverCampaignStats } from '../../../types/naver.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: NaverCampaignStats[];
  campaigns: string[];
  provider: string;
  startDate: Dayjs;
  endDate: Dayjs;
};

type CampaignMetrics = {
  click: number;
  postbackSuccess: number;
  conversionRate: number;
  appliedUnitPrice: number;
  revenue: number;
};

type PivotRow = {
  date: string;
  [campaign: string]: CampaignMetrics | string;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

const formatRate = (value: number) => `${value.toFixed(1)}%`;

const METRICS: { key: keyof CampaignMetrics; label: string }[] = [
  { key: 'click', label: '클릭수' },
  { key: 'postbackSuccess', label: '포스트백 성공' },
  { key: 'conversionRate', label: 'CVR' },
  { key: 'appliedUnitPrice', label: '적용 단가' },
  { key: 'revenue', label: '매출' },
];

export default function NaverCampaignTable({ data, campaigns, provider, startDate, endDate }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);

  const filename = useMemo(
    () => `naver_campaign_${provider}_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`,
    [provider, startDate, endDate]
  );

  const pivotData = useMemo<PivotRow[]>(() => {
    const map = new Map<string, PivotRow>();
    data.forEach((row) => {
      if (!map.has(row.date)) {
        map.set(row.date, { date: row.date });
      }
      const entry = map.get(row.date);
      if (entry) {
        entry[row.campaign] = {
          click: row.click,
          postbackSuccess: row.postbackSuccess,
          conversionRate: row.conversionRate,
          appliedUnitPrice: row.appliedUnitPrice,
          revenue: row.revenue,
        };
      }
    });
    return Array.from(map.values());
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(pivotData);

  const cellSx = { borderLeft: '1px solid #e2e8f0' };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu tableRef={tableRef} filename={filename} />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table ref={tableRef} size="small" sx={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            {/* 1행: 날짜 + 캠페인명 (colspan) */}
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell rowSpan={2} sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              {campaigns.map((c) => (
                <TableCell
                  key={c}
                  colSpan={METRICS.length}
                  align="center"
                  sx={{ ...cellSx, fontWeight: 700 }}
                >
                  {c}
                </TableCell>
              ))}
            </TableRow>
            {/* 2행: 지표명 반복 */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {campaigns.map((c) =>
                METRICS.map((m) => (
                  <TableCell
                    key={`${c}-${m.key}`}
                    align="right"
                    sx={m.key === 'click' ? cellSx : undefined}
                  >
                    {m.label}
                  </TableCell>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell>{row.date}</TableCell>
                {campaigns.map((c) => {
                  const metrics = row[c] as CampaignMetrics | undefined;
                  return METRICS.map((m) => (
                    <TableCell
                      key={`${c}-${m.key}`}
                      align="right"
                      sx={{
                        ...(m.key === 'click' ? cellSx : undefined),
                        ...(m.key === 'appliedUnitPrice' ? { fontWeight: 700 } : undefined),
                        ...(m.key === 'revenue' ? { fontWeight: 700, color: '#64748b' } : undefined),
                      }}
                    >
                      {metrics == null
                        ? '-'
                        : m.key === 'revenue' || m.key === 'appliedUnitPrice'
                        ? formatCurrency(metrics[m.key])
                        : m.key === 'conversionRate'
                        ? formatRate(metrics[m.key])
                        : formatNumber(metrics[m.key])}
                    </TableCell>
                  ));
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
